import React from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {REVIEWABLE_QUERY} from 'notebook/reviews/useReviewController';
import notifications from 'notifications';

/**
 * Clears dependent state when the modal owner changes. Skips the first render so edit flows can
 * keep their initial review-process selection until the user actually changes owner.
 *
 * @package
 */
export function useResetOnOwnerChange(ownerId: string, onReset: () => void): void {
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onReset();
  }, [ownerId, onReset]);
}

export const REVIEW_PROCESS_SAVE_ERROR_MESSAGE =
  "Sorry, the review process couldn't be updated, but your other changes saved. Reopen settings to try again.";

/**
 * Invalidates Apollo cache after a collection's review processes are saved via REST/Celery.
 * Evicts collection-level `reviewLifecycles` and refetches active ReviewableQuery instances so
 * collection pages, settings modals, and open review badges see the updated process list.
 *
 * @package
 */
export async function invalidateReviewLifecyclesCacheForCollections(
  collectionIds: Array<string>
): Promise<void> {
  if (collectionIds.length === 0) {
    return;
  }

  const client = getApolloClient();
  await client.refetchQueries({
    include: [REVIEWABLE_QUERY],
    updateCache(cache) {
      collectionIds.forEach((collectionId) => {
        cache.evict({
          id: cache.identify({__typename: 'TemplateCollectionNode', id: collectionId}),
          fieldName: 'reviewLifecycles',
        });
      });
      cache.gc();
    },
  });
}

/**
 * Fire-and-forget cache invalidation after collection review processes change via REST/Celery.
 */
export function evictReviewLifecyclesFromCacheForCollections(collectionIds: Array<string>): void {
  invalidateReviewLifecyclesCacheForCollections(collectionIds).catch(() => {});
}

/**
 * Saves the collection's review process assignment and refreshes the Apollo cache. The main
 * collection create/update has already succeeded by the time this runs, so a failure here is
 * swallowed after notifying the user, letting the overall save flow complete. Pass `successMessage`
 * to surface a success toast (used by flows that save the review process fire-and-forget, so the
 * outcome isn't otherwise visible).
 *
 * @package
 */
export function persistReviewLifecycle(
  collectionId: string,
  reviewLifecycleIds: Set<string>,
  {successMessage}: {successMessage?: string} = {}
): Promise<void> {
  return TemplateCollectionDAO.setReviewLifecycle(collectionId, [...reviewLifecycleIds])
    .then(() => invalidateReviewLifecyclesCacheForCollections([collectionId]).catch(() => {}))
    .then(() => {
      if (successMessage != null) {
        notifications.success(successMessage);
      }
    })
    .catch(() => {
      notifications.error(REVIEW_PROCESS_SAVE_ERROR_MESSAGE);
    });
}
